import { useState, useCallback } from 'react';
import { useSnackbar } from 'notistack';

interface UploadState {
  file: File | null;
  fileName: string;
  uploading: boolean;
  progress: number;
  status: string | null;
  error: string | null;
}

const INITIAL_STATE: UploadState = {
  file: null,
  fileName: '',
  uploading: false,
  progress: 0,
  status: null,
  error: null,
};

const resetProgress = (set: (s: Partial<UploadState>) => void) => {
  set({ status: null, progress: 0, uploading: false });
};

/**
 * Generic bulk upload hook.
 * @param uploadFn   — the API call that accepts a File and returns the response
 * @param onSuccess  — called after a successful upload (e.g. reload the list)
 * @param getStatus  — extracts a success status string from the response entity
 */
export function useBulkUpload(
  uploadFn: (file: File) => Promise<any>,
  onSuccess: () => Promise<void>,
  getStatus?: (entity: any) => string,
) {
  const [state, setStateRaw] = useState<UploadState>(INITIAL_STATE);
  const { enqueueSnackbar } = useSnackbar();

  const setState = useCallback(
    (partial: Partial<UploadState>) => setStateRaw((prev) => ({ ...prev, ...partial })),
    []
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selected = e.target.files?.[0];
      if (selected) setState({ file: selected, fileName: selected.name, error: null });
    },
    [setState]
  );

  const clearFile = useCallback(() => {
    setState({ file: null, fileName: '', error: null, status: null, progress: 0 });
  }, [setState]);

  const handleUpload = useCallback(async () => {
    if (!state.file) return;

    setState({ uploading: true, progress: 10, error: null, status: 'Uploading file to server...' });

    let simulatedProgress = 30;
    const interval = setInterval(() => {
      simulatedProgress = Math.min(simulatedProgress + 5, 70);
      setState({ progress: simulatedProgress });
      if (simulatedProgress >= 70) clearInterval(interval);
    }, 300);

    try {
      const json = await uploadFn(state.file);
      clearInterval(interval);

      const entity = json?.data;

      // Row-level validation errors
      if (entity?.errors?.length > 0) {
        const message = entity.errors
          .map((e: any) => `• Row ${e.row}, Field ${e.field}: ${e.message}`)
          .join('\n');
        setState({ error: `Validation errors:\n\n${message}` });
        resetProgress(setState);
        return;
      }

      // File/template-level errors
      if (json?.error) {
        setState({ error: json.error });
        resetProgress(setState);
        return;
      }

      // Success
      const statusMsg = getStatus
        ? getStatus(entity)
        : `Upload complete — Inserted: ${entity?.inserted ?? 0}, Updated: ${entity?.updated ?? 0}, Skipped: ${entity?.skipped ?? 0}`;

      setState({ progress: 100, error: null, status: statusMsg });
      enqueueSnackbar('Bulk upload completed successfully!', { variant: 'success' });
      await onSuccess();
    } catch (err) {
      clearInterval(interval);
      console.error(err);
      setState({ error: `Upload failed — ${err}` });
      resetProgress(setState);
    } finally {
      setState({ uploading: false });
    }
  }, [state.file, setState, uploadFn, onSuccess, getStatus, enqueueSnackbar]);

  return { uploadState: state, handleFileChange, handleUpload, clearFile };
}